import { IArchivingContractMetadata } from '@app/modules/contract/config/archiving-contract.config';
import { IElsterContractMetadata }    from '@app/modules/contract/config/elster-contract.config';
import { IFiskalContractMetadata }    from '@app/modules/contract/config/fiskal-contract.config';
import { ContractEntityConfig }       from '@app/modules/contract/presets/contract-entity-config.model';
import { ArchivingContractService }   from '@app/modules/contract/services/archiving-contract.service';
import { ElsterContractService }      from '@app/modules/contract/services/elster-contract.service';
import { FiskalContractService }      from '@app/modules/contract/services/fiskal-contract.service';
import { EmailDetail }                from '@app/shared/form-elements/form-email-settings/form-email-settings.model';
import { RequestState }               from '@core/interfaces/api.interface';
import { IContractEntityEvents }      from '@core/interfaces/entity.interface';
import { EntityActionEventEnum }      from '@models/entity.model';


// ENUMS #############################################################
// API enums *********************************************************
export enum ArchivingContractTypeEnum {
	LIGHT = 'light',
	STANDARD = 'standard',
	UNLIMITED = 'unlimited'
}

export enum ContractLogPeriodEnum {
	CURRENT = 'current',
	FUTURE = 'future',
	PAST = 'past'
}

export enum ContractStatusEnum {
	ACTIVE = 'active',
	DELETED = 'deleted',
	DISABLED = 'disabled'
}

export enum ContractTypeGroupEnum {
	A = 'A',
	G = 'G',
	S = 'S',
	V = 'V'
}

// UI enums *********************************************************
export enum ContractPresetEnum {
	CONTRACT_ARCHIVING = 'contractArchiving',
	CONTRACT_ELSTER = 'contractElster',
	CONTRACT_FISKAL = 'contractFiskal',
	MY_ACCOUNT_CONTRACT_ARCHIVING = 'myAccountContractArchiving',
	MY_ACCOUNT_CONTRACT_ELSTER = 'myAccountContractElster',
	MY_ACCOUNT_CONTRACT_FISKAL = 'myAccountContractFiskal',
	MY_ACCOUNT_CONTRACT_ARCHIVING_DETAIL_VIEW = 'myAccountContractArchivingDetailView',
	MY_ACCOUNT_CONTRACT_ELSTER_DETAIL_VIEW = 'myAccountContractElsterDetailView',
	MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW = 'myAccountContractFiskalDetailView'
}

// CONSTANTS #########################################################
export const ARCHIVING_TIER_LEVEL: Record<ArchivingContractTypeEnum, number> = {
	[ ArchivingContractTypeEnum.LIGHT ]: 1,
	[ ArchivingContractTypeEnum.STANDARD ]: 2,
	[ ArchivingContractTypeEnum.UNLIMITED ]: 3
};

// TYPES #############################################################
export type ContractMetadataTypes =
	IArchivingContractMetadata
	| IElsterContractMetadata
	| IFiskalContractMetadata;

export type ContractProductTypes =
	ArchivingContract |
	ElsterContract |
	FiskalContract;

export type ContractProductPropertiesLogTypes =
	ArchivingContractPropertiesLog |
	ElsterContractPropertiesLog |
	FiskalContractPropertiesLog;

export type ContractProductPropertiesLogKeyTypes =
	keyof ArchivingContractPropertiesLog |
	keyof ElsterContractPropertiesLog |
	keyof FiskalContractPropertiesLog;

export type ContractServiceTypes =
	ArchivingContractService
	| ElsterContractService
	| FiskalContractService;

export type ContractProp = ( keyof ArchivingContract | keyof ElsterContract | keyof FiskalContract );
export type ContractProps = ContractProp[];

// INTERFACES ########################################################
// API interfaces ****************************************************

// UI interfaces *****************************************************
export interface IContractEntityActionEvent {
	action: EntityActionEventEnum;
	contract: ContractProductTypes;
	contractEntityConfig?: ContractEntityConfig;
}


// ENTITIES ##########################################################
// MODEL USED FOR TRANSITION APP<->API (TransitionalModelObject)
export class ContractFormTMO {
	public contractChangeLog?: ContractProductPropertiesLogTypes = undefined;
	public customerIdentifier: string = undefined;
	public customerName: string = undefined;
	public notifications?: EmailDetail[] = [];
}

// Base
class BaseContractTMO {
	public createdBy?: string = undefined;
	public createdDate?: Date = undefined;
	public customerName: string = undefined;
	public customerIdentifier: string = undefined;
	public id: string = undefined;
	public lastModifiedBy?: string = undefined;
	public lastModifiedDate?: Date = undefined;
	public notifications: EmailDetail[] = undefined;
	public status: ContractStatusEnum = undefined;
}

class BaseContractPropertiesLogTMO {
	public customerName: string = undefined;
	public from: Date = undefined;
	public projectNumber: string = undefined;
	public status: ContractStatusEnum = undefined;
	public to: Date = undefined;
}

// Fiskal
export class FiskalAllContractPropertiesLogTMO extends BaseContractPropertiesLogTMO {
	public businessPremisesLimit?: number = undefined;
	public contractedBusinessPremises?: number = undefined;
	public contractedCloudErs?: number = undefined;
	public contractedFccDataCenterErs?: number = undefined;
	public contractedTokenErs?: number = undefined;
	public contractTypeGroup: ContractTypeGroupEnum = undefined;
	public fccDataCenterContractTypeGroup: ContractTypeGroupEnum = undefined;
	public fccDataCenterContractedBusinessPremises?: number = undefined;
	public fccDataCenterEnabled?: boolean = undefined;
	public fccDataCenterVolume?: number = undefined;
	public volume?: number = undefined;
}

export class FiskalContractTMO extends BaseContractTMO {
	public contractPropertiesLog: FiskalContractPropertiesLog[] = [];
}

export class FiskalCurrentTenantContractTMO {
	public id: string = undefined;
	public contractPropertiesLog: FiskalCurrentTenantContractPropertiesLog = undefined;
}

export class FiskalCurrentTenantContractPropertiesLogTMO {
	public fccDataCenterEnabled: boolean = undefined;
	public from: Date = undefined;
	public to: Date = undefined;
}

// Archive
export class ArchivingAllContractPropertiesLogTMO extends BaseContractPropertiesLogTMO {
	public contractedErses?: number = undefined;
	public contractType?: ArchivingContractTypeEnum = undefined;
}

export class ArchivingContractTMO extends BaseContractTMO {
	public contractPropertiesLog: ArchivingContractPropertiesLog[] = [];
}

export class ArchivingTenantContractTypeTMO {
	public cashPointClosing?: boolean = undefined;
	public contractType?: ArchivingContractTypeEnum = undefined;
	public openArchive?: boolean = undefined;
	public proceduralDocumentation?: boolean = undefined;
	public tenantId?: string = undefined;
	public tssLog?: boolean = undefined;
}

// Elster
export class ElsterAllContractPropertiesLogTMO extends BaseContractPropertiesLogTMO {}

export class ElsterContractTMO extends BaseContractTMO {
	public contractPropertiesLog: ElsterContractPropertiesLog[] = [];
}


// MODEL USED IN APPLICATION (with additional properties)
export class ContractForm extends ContractFormTMO {
	public contractPropertiesLog?: ContractProductPropertiesLogTypes = undefined;
	public etag?: string = undefined;
	public id: string = undefined;
}

// Fiskal
export class FiskalContractPropertiesLog extends FiskalAllContractPropertiesLogTMO {}

export class FiskalContract extends FiskalContractTMO {
	public etag?: string = undefined;
	public ui?: IContractEntityEvents = {
		entityActions: {},
		requestError: undefined,
		requestState: RequestState.NOT_ASKED
	};
}

export class FiskalCurrentTenantContractPropertiesLog extends FiskalCurrentTenantContractPropertiesLogTMO {}

export class FiskalCurrentTenantContract extends FiskalCurrentTenantContractTMO {}

// Archive
export class ArchivingContractPropertiesLog extends ArchivingAllContractPropertiesLogTMO {}

export class ArchivingContract extends ArchivingContractTMO {
	public etag?: string = undefined;
	public ui?: IContractEntityEvents = {
		requestError: undefined,
		entityActions: {},
		requestState: RequestState.NOT_ASKED
	};
}

export class ArchivingTenantContractType extends ArchivingTenantContractTypeTMO {}

// Elster
export class ElsterContractPropertiesLog extends ElsterAllContractPropertiesLogTMO {}

export class ElsterContract extends ElsterContractTMO {
	public etag?: string = undefined;
	public ui?: IContractEntityEvents = {
		requestError: undefined,
		entityActions: {},
		requestState: RequestState.NOT_ASKED
	};
}
